import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, ElementRef, inject, OnDestroy, signal } from '@angular/core';
import { ActivityService, AlertService, EclassAnswerService, NotificationService, StudentService } from '@app/core';
import { Activity, Answer, Attachment, StudentSummary } from '@app/core/models';
import { DatepickerComponent, Popover, SHARED_MODULES, Strategy } from '@app/shared';
import { AppState } from '@app/store/app.state';
import { Store } from '@ngrx/store';
import { ModalReviewService } from './modal-review.service';
import { AttachmentService } from '@app/core/services/attachment.service';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { AppSelectors } from '@app/store';
import {
  BehaviorSubject,
  combineLatest,
  exhaustMap,
  filter,
  finalize,
  map,
  of,
  startWith,
  Subject,
  switchMap,
  take,
  takeUntil,
} from 'rxjs';
import { isExternal, notNull, safeEmpty, safeEmptyList, sanitize, Vimeo } from '@app/shared/utils';
import { first, head, isEmpty, last, takeRightWhile, takeWhile } from 'lodash';
import { format } from 'date-fns';
import { portalConst } from '@app/config';
import { FileViewerComponent } from '@app/shared/modules/file-viewer/file-viewer.component';
import { UserPhotoComponent } from '@app/shared/modules/user-photo/user-photo.component';
import { ModalReviewScoreComponent } from './modal-review-score.component';
import { ModalReviewPopoverAnswersComponent } from './modal-review-popover-answers.component';
import { ModalReviewPopoverFilterStudentsComponent } from './modal-review-popover-filter-students.component';
import { ModalReviewFilesComponent } from './modal-review-files.component';
import { ModalReviewFeedbackComponent } from './modal-review-feedback.component';

interface StudentSummaryCustom extends StudentSummary {
  group_by: string;
}

enum ConcludedGroup {
  Student = 1,
  Responsible = 2,
  Teacher = 3,
}

export const STUDENT_STATES = {
  completed: 'completed',
  corrected: 'corrected',
  pending: 'pending',
  all: 'all',
};

@Component({
  imports: [
    SHARED_MODULES,
    FileViewerComponent,
    UserPhotoComponent,
    ModalReviewScoreComponent,
    ModalReviewFilesComponent,
    ModalReviewFeedbackComponent,
  ],
  templateUrl: './modal-review.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalReviewComponent implements OnDestroy {
  private readonly _dialogRef = inject<DialogRef<string>>(DialogRef);
  private readonly _store = inject<Store<AppState>>(Store);
  private readonly _activityService = inject(ActivityService);
  private readonly _studentService = inject(StudentService);
  private readonly _popover = inject(Popover);
  private readonly _modalReview = inject(ModalReviewService);
  private readonly _alertService = inject(AlertService);
  private readonly _answerService = inject(EclassAnswerService);
  private readonly _modalReviewService = inject(ModalReviewService);
  private readonly _notificationService = inject(NotificationService);
  private readonly _attachmentService = inject(AttachmentService);
  private readonly _formBuilder = inject(FormBuilder);
  private readonly _translateService = inject(TranslateService);
  public readonly data = inject(DIALOG_DATA);
  public isExternal$ = this._store.select(AppSelectors.isExternal).pipe(startWith(isExternal()));
  public pendingActivities$ = new BehaviorSubject<any[]>([]);
  public students$ = new BehaviorSubject<StudentSummaryCustom[]>([]);
  public activitySelected$ = new BehaviorSubject<Activity>(undefined);
  public answerStudentSelected$ = new BehaviorSubject<Answer>(undefined);
  public fileView$ = new BehaviorSubject<any>(undefined);
  public loading$ = new BehaviorSubject<boolean>(false);
  public submitted$ = new BehaviorSubject<boolean>(false);
  public destroy$ = new Subject<void>();
  public form: FormGroup;
  public searchCtrl = new FormControl();
  public activityCtrl = new FormControl(undefined);
  public studentCtrl = new FormControl(undefined);
  public observation: boolean = true;
  public lastModifyStudentId: number;
  public studentList;
  public evaluative;
  public answer = false;
  public selectStudent;
  public isBlurred = false;
  private studentFormMap: { [studentId: string]: FormGroup } = {};
  protected expand = signal<boolean>(false);

  constructor() {
    this.initForm();
    this.loadListeners();
  }

  public initForm() {
    this.form = this._formBuilder.group({
      students: this._formBuilder.array([]),
    });
  }

  private setStudents(students): void {
    this.studentList = students;
    const studentFGs = students.map((student) => {
      const group = this._formBuilder.group({
        score: [student.score],
        concluded: [student.concluded],
      });
      if (student.concluded_group === 1 || student.concluded_group === 2 || student.score !== null) {
        group.controls['concluded'].disable();
      } else {
        group.controls['concluded'].enable();
      }
      this.studentFormMap[student.id] = group;
      return group;
    });
    const studentFormArray = this._formBuilder.array(studentFGs);
    this.form.setControl('students', studentFormArray);
  }

  public onBlur(student: any): void {
    const group = this.studentFormMap[student.id];
    const changedValues = group.value;

    this.isBlurred = true;

    if (this.isBlurred) {
      this.saveState(student, changedValues);
      this.isBlurred = false;
    }
  }

  public hasAttachments(answer): boolean {
    return (
      answer?.attachments?.length > 0 ||
      answer?.feedback?.some(feedback => feedback.attachments?.length > 0)
    );
  }

  public onSwitchChange(student: any, value: boolean): void {
    const group = this.studentFormMap[student.id];
    group.patchValue({ concluded: value });
    this.saveState(student, group.value);
  }

  public getStudentFormGroup(studentId: string): FormGroup {
    return this.studentFormMap[studentId];
  }

  public onStudentSelect(student: any) {
    this.fileView$.next(undefined);
    this.selectStudent = student.id;
    const studentId = student.id;
    this.studentCtrl.setValue(studentId);
  }

  public studentState(student): any {
    switch (student) {
      case STUDENT_STATES.completed:
        return {
          border: 'border-base-post-notice',
          text: 'text-base-post-notice',
          disable: false,
        };
      case STUDENT_STATES.corrected:
        return {
          border: 'border-base-mantis-500',
          text: 'text-base-mantis-500',
        };
      case ConcludedGroup.Student:
        return {
          label: this._translateService.instant('geral.aluno-a'),
        };
      case ConcludedGroup.Teacher:
        return {
          label: this._translateService.instant('geral.professor-a'),
        };
      case ConcludedGroup.Responsible:
        return {
          label: this._translateService.instant('geral.responsavel'),
        };
      default:
        return {
          border: 'border-base-gray-blue-300',
          text: 'text-base-gray-blue-300',
          label: '',
        };
    }
  }

  public get indexOfAttachmentSelected(): number {
    return this.answerStudentSelected$.value?.attachments?.findIndex(
      (attachment) => attachment.id === this.fileView$.value.id,
    );
  }

  public get hasMultipleAttachments(): boolean {
    return this.answerStudentSelected$.value?.attachments?.length > 1;
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.students$.complete();
    this.pendingActivities$.complete();
    this.activitySelected$.complete();
    this.answerStudentSelected$.complete();
    this.fileView$.complete();
  }

  private loadListeners() {
    this.studentCtrl.valueChanges.subscribe((studentId) => {
      this._modalReview.setStudentId(studentId);
    });

    this._activityService
      .pendingAnswers()
      .pipe(
        takeUntil(this.destroy$),
        safeEmptyList(),
        map((activities) => activities.map((activity) => ({ ...activity, pending: true }))),
      )
      .subscribe((activities) => {
        this.pendingActivities$.next([this.data.activity, ...activities]);
        this.activityCtrl.setValue(first(this.pendingActivities$.value));
      });
    const activity$ = this.activityCtrl.valueChanges.pipe(notNull());
    const activeUser$ = this._store.select(AppSelectors.ActiveUser).pipe(notNull());
    combineLatest({
      activity: activity$,
      active_user: activeUser$,
      activitySelected: this.activitySelected$.pipe(notNull()),
    })
      .pipe(
        takeUntil(this.destroy$),
        switchMap(({ activity, active_user }) =>
          !isEmpty(activity.groups)
            ? this._studentService
                .getStudentsByEntity(active_user.entidade_id, {
                  group_id: activity.groups[0].id,
                  date: activity.daily.date,
                })
                .pipe(safeEmptyList())
            : of(activity.students as StudentSummary[]),
        ),
      )
      .subscribe((students) => {
        this.processStateStudents(students);
      });

    this._modalReview.answers$
      .pipe(
        notNull(),
        map((answers) => answers.find((answer) => answer.is_selected)),
      )
      .subscribe((answer) => {
        this.answerStudentSelected$.next(answer);
        this.answerStudentSelected$.pipe(takeUntil(this.destroy$)).subscribe((answer) => {
          if (answer) {
            this.selectStudent = answer.student_id;
          }
        });
        this.onSelectAttachment(first(answer?.attachments) ??
        first(answer?.feedback?.find(f => f.attachments?.length)?.attachments));
      });

    combineLatest({
      studentId: this._modalReview.studentId$.pipe(notNull()),
      activity: this.activitySelected$.pipe(notNull()),
    })
      .pipe(
        map(({ studentId, activity }) => first(activity.answers.filter((answer) => answer.student_id === +studentId))),
      )
      .subscribe((answer) => this._modalReview.selectAnswer(answer?.id));

    activity$
      .pipe(
        takeUntil(this.destroy$),
        exhaustMap((activity) => this._activityService.answers(activity.id).pipe(safeEmpty())),
      )
      .subscribe((activity) => {
        this._modalReview.setAnswers(activity?.answers);
        this.activitySelected$.next(activity);
      });
  }

  private studentsFilter(student: any, answer: any, defaultGroupBy = STUDENT_STATES.pending) {
    const score = answer?.score || null;
    const concluded = answer?.concluded || false;
    const concluded_group = answer?.concluded_group;
    const group_by =
      score !== null && concluded ? STUDENT_STATES.corrected : concluded ? STUDENT_STATES.completed : defaultGroupBy;

    return {
      ...student,
      answer_id: answer?.id,
      score,
      concluded,
      revised: answer?.revised,
      concluded_group,
      group_by,
    };
  }

  private processStateStudents(students: StudentSummary[]): void {
    const answers = this.activitySelected$.value?.answers;
    this.studentList = students.map((student) => {
      const answer = answers.find((a: any) => a.student_id === student.id);
      const filteredStudent = this.studentsFilter(student, answer, STUDENT_STATES.pending);
      return filteredStudent;
    });
    this.setStudents(this.studentList);
    this.students$.next(this.studentList);
    if (this.lastModifyStudentId) {
      this.studentCtrl.setValue(this.lastModifyStudentId);
    } else {
      this.studentCtrl.setValue(first(this.students$.value)?.id);
    }
    this.loading$.next(true);
  }

  public saveState(student, value) {
    this.submitted$.next(true);
    const hasScore = value.score !== null;
    const reviewData = {
      student_user_id: student.user_id,
      concluded: hasScore ? 1 : value.concluded,
      revised: hasScore || value.concluded ? 1 : 0,
      score: value.score,
    };

    if (student.answer_id) {
      this._answerService
        .review(student.answer_id, reviewData)
        .pipe(
          safeEmpty(),
          finalize(() => {
            this.submitted$.next(false);
          }),
        )
        .subscribe((response: any) => {
          this.updateStudentList(student, response, value);

          this._notificationService.success('geral.sucesso');
        });
    } else {
      this._answerService
        .reviewEmpty(this.activitySelected$.value.id, student.id, reviewData)
        .pipe(
          safeEmpty(),
          finalize(() => {
            this.submitted$.next(false);
          }),
        )
        .subscribe((response: any) => {
          const updatedAnswers = response.activity?.answers;
          this._modalReview.updatedAnswers(updatedAnswers, student);
          this.updateStudentList(student, response, value);

          this._notificationService.success('geral.sucesso');
        });
    }
  }

  private updateStudentList(student, response, value) {
    const hasScore = value.score !== null;
    const concluded = hasScore ? response.concluded : value.concluded;

    const index = this.studentList.findIndex((s) => s.id === student.id);
    const updatedStudentList = [...this.studentList];
    const answerForFilter = {
      id: response.id,
      score: response.score,
      concluded,
      revised: response.revised,
      concluded_group: response.concluded_group,
    };
    updatedStudentList[index] = this.studentsFilter(updatedStudentList[index], answerForFilter, STUDENT_STATES.pending);

    const updatedAnswers = [...this.activitySelected$.value.answers];
    const answerIndex = updatedAnswers.findIndex((a: any) => a.student_id === student.id);
    this.lastModifyStudentId = student.id;

    if (answerIndex !== -1) {
      updatedAnswers[answerIndex] = {
        ...updatedAnswers[answerIndex],
        score: response.score,
        concluded: response.concluded,
        revised: response.revised,
        concluded_group: response.concluded_group,
      };
    } else {
      const newAnswer = response.activity.answers.find((a: any) => a.student_id === student.id);
      updatedAnswers.push(newAnswer);
    }
    this.activitySelected$.next({
      ...this.activitySelected$.value,
      answers: updatedAnswers,
    });
    this.studentList = updatedStudentList;
    this.students$.next(updatedStudentList);
  }

  public onNextAttachment() {
    const next = takeRightWhile(
      Object.keys(this.answerStudentSelected$.value.attachments),
      (key) => key !== String(this.indexOfAttachmentSelected),
    );

    this.onSelectAttachment(this.answerStudentSelected$.value.attachments[head(next) || 0]);
  }

  public onUpdateScore(activity: Activity) {
    this.activityCtrl.setValue(this.activityCtrl.value);
  }

  public onPriorAttachment() {
    const next = takeWhile(
      Object.keys(this.answerStudentSelected$.value.attachments),
      (key) => key !== String(this.indexOfAttachmentSelected),
    );

    this.onSelectAttachment(
      this.answerStudentSelected$.value.attachments[
        last(next) || this.answerStudentSelected$.value.attachments?.length - 1
      ],
    );
  }

  public onSelectAnswer(elementRef: ElementRef) {
    this._popover
      .open<Answer>(ModalReviewPopoverAnswersComponent, {
        positionStrategy: [Strategy.BOTTOM],
        elementRef,
      })
      .closed.pipe(filter((answer) => !!answer?.id))
      .subscribe((answer) => this._modalReview.selectAnswer(answer.id));
  }

  public onFilterStudents(elementRef: ElementRef) {
    this._popover
      .open(ModalReviewPopoverFilterStudentsComponent, {
        positionStrategy: [Strategy.RIGHT],
        elementRef,
        data: this.studentList,
      })
      .closed.subscribe((group) => {
        if (Array.isArray(group)) {
          this.students$.next(group);
        }
      });
  }

  public onSelectAttachment(attachment: Attachment) {
    if (attachment) {
      this.onOpenFileViewer(attachment);
    } else {
      this.answer = false;
    }
  }

  public onClose() {
    this._dialogRef.close();
  }

  public onOpenFileViewer(attachment: Attachment) {
    let data: any;

    switch (attachment?.type) {
      case 1:
        data = this.getInfoVimeo(attachment);
        break;
      default:
        data = attachment
          ? { id: attachment.id, url: attachment.url || attachment.file, name: attachment.title, html: attachment.html }
          : undefined;
        break;
    }
    this.answer = true;
    this.fileView$.next(data);
  }

  public activitiesCustomSearchFn(term: string, item: any) {
    return (
      sanitize(item.title).indexOf(sanitize(term)) !== -1 ||
      sanitize(item.groups[0]?.cod_turma || item.groups[0]?.descricao).indexOf(sanitize(term)) !== -1
    );
  }

  public onOpenResubmissionDatepicker(answerId: string, elementRef: ElementRef) {
    const createdDate = new Date(this.answerStudentSelected$.value.created_at);

    this._popover
      .open<Date>(DatepickerComponent, {
        positionStrategy: [Strategy.LEFT_BOTTOM],
        elementRef,
        data: { showTime: true, minDate: createdDate },
      })
      .closed.pipe(
        notNull(),
        switchMap((date) =>
          combineLatest({
            answerId: of(answerId),
            date: of(date),
            result: this._alertService.confirm({
              message: 'eclass.tarefa.estender-data',
            }),
          }),
        ),
        filter(({ result }) => !!result),
      )
      .subscribe(this.saveReview);
  }

  public groupByFn = (student: any) => student.group_by;

  public groupValueFn = (_: string, students: any[]) => ({ name: students[0].group_by, total: students.length });

  private saveReview = ({ answerId, date }) => {
    this._answerService
      .review(answerId, { resubmission_date: format(date, 'yyyy-MM-dd HH::mm') })
      .pipe(safeEmpty())
      .subscribe((answer) => {
        this._modalReviewService.editAnswer(this._modalReviewService.formatAnswerItem(answer));
        this._notificationService.success('geral.sucesso');
      });
  };

  private getInfoVimeo(attachment: Attachment) {
    const videoUrl = Vimeo.getVideoUrl(attachment);
    if (!!videoUrl) {
      return {
        name: attachment?.title,
        url: videoUrl,
      };
    }

    const params = {
      id: attachment?.id,
      video: '/videos/' + Vimeo.getVideoId(attachment),
      conta: portalConst.vimeo_account,
    };

    this._attachmentService
      .checkVideo(params)
      .pipe(
        take(1),
        map((res: any) => {
          if (!res.vimeo_hash) {
            this._alertService.alert({ message: 'geral.video-renderizando' });
            this._dialogRef.close();
          }
          return Vimeo.getVideoUrl({ ...attachment, vimeo_hash: res.vimeo_hash });
        }),
      )
      .subscribe((url) => ({
        name: attachment?.title,
        url,
      }));
  }

  protected onExpand() {
    this.expand.update(value => !value);
  }
}
